import { useState, useEffect } from 'react';
import { Sparkles, Eye, Info, Copy, Check } from 'lucide-react';
import { supabaseClient } from '../../../../../lib/supabase';
import { generateAIRecommendations } from '../../../../../lib/openai';
import { isStopWord } from '../../../../../lib/stop-words';
import { toast } from '../../../../../lib/toast';
import { replaceSnippets, updateSnippetData } from '../../../../../lib/prompt-snippets';

interface AITabProps {
  analysisId: string;
  filteredMissingKeywords?: string[];
  filteredPresentKeywords?: string[];
}

const SNIPPETS = [
  { name: '{{Missing words}}', description: 'Missing keywords from Keywords tab' },
  { name: '{{Present words}}', description: 'Present keywords from Keywords tab' },
  { name: '{{Keywords}}', description: 'SEO Keywords from Keywords tab' },
  { name: '{{Headers}}', description: 'Headers with competitor domains from Competitors tab' },
  { name: '{{Text}}', description: 'Text from editor' },
  { name: '{{Resultai}}', description: 'Text from response field' },
  { name: '{{Description}}', description: 'Company description from project settings' }
];

export function AITab({ analysisId, filteredMissingKeywords = [], filteredPresentKeywords = [] }: AITabProps) {
  const [prompts, setPrompts] = useState<any[]>([]);
  const [selectedPrompt, setSelectedPrompt] = useState<string | null>(null);
  const [promptText, setPromptText] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [aiResult, setAiResult] = useState('');
  const [showSnippets, setShowSnippets] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    const loadPrompts = async () => {
      try {
        const { data, error } = await supabaseClient
          .from('prompts')
          .select('*')
          .order('category', { ascending: true })
          .order('name', { ascending: true });

        if (error) throw error;
        setPrompts(data || []);
      } catch (err) {
        console.error('Error loading prompts:', err);
        toast.error('Failed to load prompts');
      }
    };

    loadPrompts();
  }, []);

  useEffect(() => {
    if (selectedPrompt) {
      const prompt = prompts.find(p => p.id === selectedPrompt);
      if (prompt) {
        setPromptText(prompt.content);
      }
    }
  }, [selectedPrompt, prompts]);

  useEffect(() => {
    const loadAnalysisData = async () => {
      try {
        const { data: analysis, error } = await supabaseClient
          .from('analyses')
          .select(`
            *,
            project:projects (
              id,
              name,
              description
            )
          `)
          .eq('id', analysisId)
          .single();

        if (error) throw error;

        // Update snippet data with project description
        const projectDescription = analysis?.project?.description || 'No description available';
        updateSnippetData({
          description: projectDescription
        });

        // Update keywords data - використовуємо дані з KeywordsTab напряму
        console.log('🟢 AITab - Using filtered keywords from KeywordsTab');
        console.log('🟢 AITab - Missing keywords from KeywordsTab:', filteredMissingKeywords);
        console.log('🟢 AITab - Present keywords from KeywordsTab:', filteredPresentKeywords);

        updateSnippetData({
          keywords: {
            missing: filteredMissingKeywords,
            present: filteredPresentKeywords,
            seo: analysis?.additional_keywords || []
          }
        });

        // Update headers data
        if (analysis?.content_analysis?.competitors) {
          const selectedDomains = analysis.content_analysis.selectedDomains || [];
          
          const headers = analysis.content_analysis.competitors
            .filter((competitor: any) => 
              competitor?.domain && selectedDomains.includes(competitor.domain)
            )
            .flatMap((competitor: any) => {
              if (!competitor?.headers) return [];
              
              return Object.entries(competitor.headers).map(([level, texts]: [string, any]) => 
                texts.map((text: string) => `${competitor.domain} (${level}): ${text}`)
              ).flat();
            });

          updateSnippetData({ headers });
        }

        // Update editor content
        if (analysis?.editor_content) {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = analysis.editor_content;
          updateSnippetData({
            text: tempDiv.textContent?.trim() || ''
          });
        }

        // Set AI result if it exists
        if (analysis?.ai_result) {
          setAiResult(analysis.ai_result);
        }

      } catch (err) {
        console.error('Error loading analysis data:', err);
        toast.error('Failed to load analysis data');
      }
    };

    loadAnalysisData();
  }, [analysisId]);

  // Оновлюємо snippet data коли змінюються відфільтровані ключі з KeywordsTab
  useEffect(() => {
    console.log('🟢 AITab - Keywords from KeywordsTab changed');
    console.log('🟢 AITab - Missing:', filteredMissingKeywords);
    console.log('🟢 AITab - Present:', filteredPresentKeywords);
    
    updateSnippetData({
      keywords: {
        missing: filteredMissingKeywords,
        present: filteredPresentKeywords,
        seo: [] // Additional keywords залишаємо як є
      }
    });
  }, [filteredMissingKeywords, filteredPresentKeywords]);

  const insertSnippet = (snippet: { name: string }) => {
    const textArea = document.querySelector('textarea');
    if (!textArea) return;

    const start = textArea.selectionStart;
    const end = textArea.selectionEnd;
    const text = promptText;
    const newText = text.substring(0, start) + snippet.name + text.substring(end);
    setPromptText(newText);
  };

  const handlePreviewPrompt = () => {
    if (!promptText.trim()) {
      toast.error('Please enter or select a prompt first');
      return;
    }

    try {
      const processedPrompt = replaceSnippets(promptText);
      setPromptText(processedPrompt);
      toast.success('Prompt preview updated with actual data');
    } catch (err) {
      console.error('Error previewing prompt:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to preview prompt');
    }
  };

  const generateRecommendations = async () => {
    if (!promptText.trim() || isGenerating) return;

    setIsGenerating(true);
    const loadingToast = toast.loading('Generating AI recommendations...');

    try {
      const result = await generateAIRecommendations(promptText);
      setAiResult(result);

      const { error: updateError } = await supabaseClient
        .from('analyses')
        .update({ ai_result: result })
        .eq('id', analysisId);

      if (updateError) throw updateError;

      toast.success('AI recommendations generated!', { id: loadingToast });
    } catch (err) {
      console.error('Error generating AI recommendations:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to generate AI recommendations', { id: loadingToast });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopyResult = () => {
    if (!aiResult) return;
    
    // Create a temporary div to extract text from HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = aiResult;
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    
    navigator.clipboard.writeText(textContent)
      .then(() => {
        setIsCopied(true);
        toast.success('Content copied to clipboard');
        
        // Reset the copied state after 2 seconds
        setTimeout(() => {
          setIsCopied(false);
        }, 2000);
      })
      .catch(err => {
        console.error('Failed to copy content:', err);
        toast.error('Failed to copy content');
      });
  };

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center">
            <Info className="h-5 w-5 text-blue-500 mr-2" />
            <h3 className="text-sm font-medium text-blue-900">Available Snippets</h3>
          </div>
          <button
            onClick={() => setShowSnippets(!showSnippets)}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            {showSnippets ? 'Hide' : 'Show'} Details
          </button>
        </div>
        {showSnippets && (
          <div className="mt-2 space-y-2">
            {SNIPPETS.map((snippet) => (
              <div key={snippet.name} className="flex items-start space-x-2 text-sm">
                <button
                  onClick={() => insertSnippet(snippet)}
                  className="px-2 py-1 bg-blue-100 rounded text-blue-800 font-mono hover:bg-blue-200"
                >
                  {snippet.name}
                </button>
                <span className="text-blue-700">-</span>
                <span className="text-blue-800 flex-1">{snippet.description}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Prompt
          </label>
          <select
            value={selectedPrompt || ''}
            onChange={(e) => setSelectedPrompt(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          >
            <option value="">Choose a prompt...</option>
            {prompts.map((prompt) => (
              <option key={prompt.id} value={prompt.id}>
                {prompt.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Prompt Text
            </label>
            <button
              onClick={handlePreviewPrompt}
              className="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-600 hover:text-indigo-800"
              title="Preview with actual data"
            >
              <Eye className="h-3 w-3 mr-1" />
              Preview
            </button>
          </div>
          <textarea
            value={promptText}
            onChange={(e) => setPromptText(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm resize-y"
            placeholder="Enter or select a prompt..."
            rows={6}
          />
        </div>
      </div>

      <button
        onClick={generateRecommendations}
        disabled={!promptText.trim() || isGenerating}
        className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
      >
        <Sparkles className="h-4 w-4 mr-2" />
        Generate Recommendations
      </button>

      {aiResult && (
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Result
            </label>
            <button
              onClick={handleCopyResult}
              className="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-600 hover:text-indigo-800"
              title="Copy result to clipboard"
            >
              {isCopied ? (
                <Check className="h-3 w-3 mr-1" />
              ) : (
                <Copy className="h-3 w-3 mr-1" />
              )}
              {isCopied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <div 
            className="prose prose-sm max-w-none p-4 border border-gray-200 rounded-md bg-gray-50 min-h-[200px] overflow-y-auto"
            dangerouslySetInnerHTML={{ __html: aiResult }}
          />
        </div>
      )}
    </div>
  );
}